import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";

export default function EnquiryModal({ property, open, onClose }) {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", city: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const title = property
    ? (lang === "hi" && property.title_hi) || property.title_en
    : "";

  const buildWhatsApp = (name, phone, city, message) => {
    const text = encodeURIComponent(
      `Richcube Property Enquiry\n` +
        `Name: ${name}\n` +
        `Phone: ${phone}\n` +
        `City: ${city}\n` +
        `Property: ${title || "General"}\n` +
        `Message: ${message || "-"}`
    );
    return `https://wa.me/919203809569?text=${text}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city || property?.city || "",
        message: form.message,
        property_id: property?.id || null,
      });
      toast.success(t.enquiry.success);
      const wa = data?.whatsapp_link || buildWhatsApp(form.name, form.phone, form.city, form.message);
      window.open(wa, "_blank", "noopener");
      onClose?.();
      setForm({ name: "", phone: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const quickWhatsApp = () => {
    const wa = buildWhatsApp(
      form.name || "Prospect",
      form.phone || "",
      form.city || property?.city || "",
      form.message
    );
    window.open(wa, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="enquiry-modal"
        className="sm:max-w-lg bg-white border-[#EBEFF5]"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0B2545]">
            {t.enquiry.title}
          </DialogTitle>
          <DialogDescription className="text-[#1F2A44]">
            {t.enquiry.subtitle}
            {title ? (
              <>
                <br />
                <span className="text-[#0B2545] font-semibold mt-1 inline-block">
                  {title}
                </span>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="enq-name" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.enquiry.name}
            </Label>
            <Input
              id="enq-name"
              data-testid="enquiry-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Rahul Mehta"
              className="h-11 border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>
          <div>
            <Label htmlFor="enq-phone" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.enquiry.phone}
            </Label>
            <Input
              id="enq-phone"
              data-testid="enquiry-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              placeholder="+91 98765 43210"
              className="h-11 border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>
          <div>
            <Label htmlFor="enq-city" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.enquiry.city}
            </Label>
            <Input
              id="enq-city"
              data-testid="enquiry-city-input"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder={property?.city || "Indore / Goa"}
              className="h-11 border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>
          <div>
            <Label htmlFor="enq-message" className="text-xs uppercase tracking-wider text-[#1F2A44]">
              {t.enquiry.message}
            </Label>
            <Textarea
              id="enq-message"
              data-testid="enquiry-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              className="border-[#EBEFF5] focus-visible:ring-[#C9A84C]"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-3 flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={quickWhatsApp}
              data-testid="whatsapp-cta-button"
              className="border-[#25D366] text-[#1EAE52] hover:bg-[#25D366] hover:text-white font-semibold"
            >
              <MessageCircle size={16} className="mr-2" />
              {t.enquiry.whatsapp_cta}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="enquiry-submit-btn"
              className="bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] hover:text-[#E9D58F] font-semibold"
            >
              <Send size={16} className="mr-2" />
              {submitting ? "..." : t.enquiry.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
